import React from 'react';
import type { Editor } from '@tiptap/react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  CodeXml,
  Highlighter,
  Italic,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
  Undo2,
} from 'lucide-react';
import clsx from 'clsx';

import classes from './TiptapAboutEditor.module.scss';

type Props = {
  editor: Editor;
  disabled?: boolean;
};

type HeadingValue = 'paragraph' | '1' | '2' | '3';

const ICON_SIZE = 17;
const ICON_STROKE = 1.75;

const ToolbarButton: React.FC<{
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ active, disabled, onClick, title, children }) => (
  <button
    type='button'
    className={clsx(classes.toolbarButton, active && classes.toolbarButtonActive)}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <span className={classes.toolbarDivider} aria-hidden />;

const getHeadingValue = (editor: Editor): HeadingValue => {
  if (editor.isActive('heading', { level: 1 })) return '1';
  if (editor.isActive('heading', { level: 2 })) return '2';
  if (editor.isActive('heading', { level: 3 })) return '3';
  return 'paragraph';
};

const getHeadingLabel = (value: HeadingValue): string => {
  switch (value) {
    case '1':
      return 'Заголовок 1';
    case '2':
      return 'Заголовок 2';
    case '3':
      return 'Заголовок 3';
    default:
      return 'Абзац';
  }
};

export const AboutEditorToolbar: React.FC<Props> = ({ editor, disabled = false }) => {
  const headingValue = getHeadingValue(editor);

  const setHeading = (value: HeadingValue) => {
    const chain = editor.chain().focus();
    if (value === 'paragraph') {
      chain.setParagraph().run();
      return;
    }
    chain.toggleHeading({ level: Number(value) as 1 | 2 | 3 }).run();
  };

  const iconProps = {
    size: ICON_SIZE,
    strokeWidth: ICON_STROKE,
    className: classes.toolbarIcon,
    'aria-hidden': true as const,
  };

  return (
    <div className={classes.toolbar}>
      <div className={classes.toolbarRow}>
        <div className={classes.toolbarGroup}>
          <ToolbarButton
            title='Отменить'
            disabled={disabled || !editor.can().chain().focus().undo().run()}
            onClick={() => editor.chain().focus().undo().run()}
          >
            <Undo2 {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Повторить'
            disabled={disabled || !editor.can().chain().focus().redo().run()}
            onClick={() => editor.chain().focus().redo().run()}
          >
            <Redo2 {...iconProps} />
          </ToolbarButton>
        </div>

        <ToolbarDivider />

        <div className={classes.toolbarGroup}>
          <label className={classes.headingSelectWrap}>
            <span className={classes.srOnly}>Стиль абзаца</span>
            <select
              className={classes.headingSelect}
              value={headingValue}
              disabled={disabled}
              aria-label='Стиль абзаца'
              onChange={(event) => setHeading(event.target.value as HeadingValue)}
            >
              <option value='paragraph'>Абзац</option>
              <option value='1'>Заголовок 1</option>
              <option value='2'>Заголовок 2</option>
              <option value='3'>Заголовок 3</option>
            </select>
            <span className={classes.headingSelectLabel}>{getHeadingLabel(headingValue)}</span>
          </label>
        </div>

        <ToolbarDivider />

        <div className={classes.toolbarGroup}>
          <ToolbarButton
            title='Жирный'
            active={editor.isActive('bold')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Курсив'
            active={editor.isActive('italic')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Подчеркнутый'
            active={editor.isActive('underline')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <Underline {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Зачеркнутый'
            active={editor.isActive('strike')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Strikethrough {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Код'
            active={editor.isActive('code')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <Code {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Выделение'
            active={editor.isActive('highlight')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          >
            <Highlighter {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Надстрочный'
            active={editor.isActive('superscript')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
          >
            <Superscript {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Подстрочный'
            active={editor.isActive('subscript')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleSubscript().run()}
          >
            <Subscript {...iconProps} />
          </ToolbarButton>
        </div>
      </div>

      <div className={classes.toolbarRow}>
        <div className={classes.toolbarGroup}>
          <ToolbarButton
            title='По левому краю'
            active={editor.isActive({ textAlign: 'left' })}
            disabled={disabled}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <AlignLeft {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='По центру'
            active={editor.isActive({ textAlign: 'center' })}
            disabled={disabled}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <AlignCenter {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='По правому краю'
            active={editor.isActive({ textAlign: 'right' })}
            disabled={disabled}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <AlignRight {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='По ширине'
            active={editor.isActive({ textAlign: 'justify' })}
            disabled={disabled}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          >
            <AlignJustify {...iconProps} />
          </ToolbarButton>
        </div>

        <ToolbarDivider />

        <div className={classes.toolbarGroup}>
          <ToolbarButton
            title='Маркированный список'
            active={editor.isActive('bulletList')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Нумерованный список'
            active={editor.isActive('orderedList')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered {...iconProps} />
          </ToolbarButton>
        </div>

        <ToolbarDivider />

        <div className={classes.toolbarGroup}>
          <ToolbarButton
            title='Цитата'
            active={editor.isActive('blockquote')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Quote {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Блок кода'
            active={editor.isActive('codeBlock')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <CodeXml {...iconProps} />
          </ToolbarButton>
          <ToolbarButton
            title='Разделитель'
            disabled={disabled}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <Minus {...iconProps} />
          </ToolbarButton>
        </div>
      </div>
    </div>
  );
};
